/* Admin-rendered navigation. Search and cart remain owned by their existing integrations. */
(() => {
  'use strict';

  if (customElements.get('dsp-navigation')) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const isVisible = (element) => element instanceof HTMLElement && element.isConnected && !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';

  class DSPNavigation extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.desktop = window.matchMedia('(min-width: 1100px)');
      this.drawer = this.querySelector('dialog[data-nav-drawer]');
      this.pages = Array.from(this.querySelectorAll('[data-nav-page]'));
      this.panels = Array.from(this.querySelectorAll('[data-nav-panel]'));
      this.triggers = Array.from(this.querySelectorAll('[data-nav-trigger]'));
      this.currentPage = 'home';
      this.openPanel = null;
      this.panelTrigger = null;
      this.drawerOpener = null;
      this.scrollLock = null;
      this.pointerTimer = null;
      this.openMethod = null;
      this.sticky = this.closest('sticky-header');
      this.headerSection = this.closest('.section-header');
      this.stickyState = null;
      this.setAttribute('data-nav-ready', '');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((panel) => { panel.hidden = true; });

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('pointerover', (event) => this.onPointerOver(event), { signal });
      this.addEventListener('pointerenter', () => this.clearPointerTimer(), { signal });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.clearPointerTimer();
        this.pointerTimer = window.setTimeout(() => this.closePanel(), 160);
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closePanel();
      }, { signal });
      this.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.closePanel();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openPanel && !this.drawer?.open) {
          event.preventDefault();
          this.closePanel({ restoreFocus: true });
        }
      }, { signal });
      document.addEventListener('dsp:before-open', (event) => this.onAssistantOpen(event), { signal });
      document.addEventListener('dsp:nav-close', () => this.closeAll({ restoreFocus: false }), { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this || event.target?.contains(this) || String(event.detail?.sectionId) === this.dataset.sectionId) this.teardown();
      }, { signal });
      this.breakpointListener = () => {
        this.closeAll({ restoreFocus: false });
        this.queueStickyUpdate({ measure: true });
      };
      this.desktop.addEventListener('change', this.breakpointListener);

      if (this.drawer) {
        this.drawer.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeDrawer({ restoreFocus: true });
        }, { signal });
        this.drawer.addEventListener('click', (event) => {
          if (event.target !== this.drawer) return;
          const bounds = this.drawer.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDrawer({ restoreFocus: true });
        }, { signal });
        // Native close is queued. Never restore focus here: the assistant may already be open.
        this.drawer.addEventListener('close', () => {
          if (this.drawer.open) return;
          this.releaseScrollLock();
          this.removeAttribute('data-drawer-open');
          this.setMobileExpanded(false);
          this.drawerOpener = null;
          this.syncStickyState();
        }, { signal });
      }

      this.setupCompactSticky(signal);
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.closeAll({ restoreFocus: false });
      this.clearPointerTimer();
      window.cancelAnimationFrame(this.stickyFrame);
      this.stickyObserver?.disconnect();
      this.flowSpacer?.remove();
      this.flowSpacer = null;
      this.headerSection?.style.removeProperty('--dsp-nav-expanded-height');
      this.headerSection?.classList.remove('scrolled-past-header');
      this.abortController?.abort();
      this.desktop?.removeEventListener('change', this.breakpointListener);
      this.abortController = null;
      this.stickyFrame = null;
      this.removeAttribute('data-nav-sticky');
      this.removeAttribute('data-nav-compact');
      this.removeAttribute('data-nav-measuring');
      this.removeAttribute('data-nav-ready');
    }

    setupCompactSticky(signal) {
      this.compactStickyEnabled = this.sticky?.getAttribute('data-sticky-type') === 'always' && Boolean(this.headerSection);
      if (!this.compactStickyEnabled) return;
      this.setAttribute('data-nav-sticky', '');
      this.flowSpacer = document.createElement('div');
      this.flowSpacer.className = 'dsp-nav__flow-spacer';
      this.flowSpacer.setAttribute('aria-hidden', 'true');
      this.flowSpacer.style.height = '0px';
      this.headerSection.after(this.flowSpacer);
      this.pendingStickyMeasure = true;

      window.addEventListener('scroll', () => this.queueStickyUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.queueStickyUpdate({ measure: true }), { passive: true, signal });
      document.addEventListener('close', () => this.queueStickyUpdate({ measure: true }), { capture: true, signal });
      this.addEventListener('focusin', () => this.queueStickyUpdate(), { signal });
      this.addEventListener('focusout', () => this.queueStickyUpdate(), { signal });
      this.addEventListener('load', (event) => {
        if (event.target instanceof HTMLImageElement) this.queueStickyUpdate({ measure: true });
      }, { capture: true, signal });

      if ('ResizeObserver' in window) {
        this.stickyObserver = new ResizeObserver(() => {
          if (this.offsetWidth !== this.observedNavWidth || this.offsetHeight !== this.observedNavHeight) this.queueStickyUpdate({ measure: true });
        });
        this.stickyObserver.observe(this);
      }
      if (document.fonts) {
        document.fonts.addEventListener('loadingdone', () => this.queueStickyUpdate({ measure: true }), { signal });
        const controller = this.abortController;
        document.fonts.ready.then(() => {
          if (this.isConnected && this.abortController === controller) this.queueStickyUpdate({ measure: true });
        });
      }
      this.queueStickyUpdate({ measure: true });
    }

    queueStickyUpdate({ measure = false } = {}) {
      if (!this.compactStickyEnabled || !this.abortController) {
        if (measure && this.isConnected) this.sticky?.setHeaderHeight?.();
        return;
      }
      this.pendingStickyMeasure = this.pendingStickyMeasure || measure;
      if (this.stickyFrame) return;
      this.stickyFrame = window.requestAnimationFrame(() => {
        this.stickyFrame = null;
        if (this.isConnected) this.updateCompactSticky();
      });
    }

    stickyLayoutIsBusy() {
      if (this.openPanel || this.drawer?.open || document.body.style.position === 'fixed' || document.querySelector('dialog[open]')) return true;
      const focused = document.activeElement;
      if (!(focused instanceof Element) || !this.contains(focused)) return false;
      let keyboardFocus = document.documentElement.dataset.dspFocus === 'keyboard';
      try { keyboardFocus = keyboardFocus || focused.matches(':focus-visible'); }
      catch { /* Older engines use the recorded keyboard modality. */ }
      // Preserve keyboard/AT focus without keeping pointer-focused rows expanded.
      return keyboardFocus && Boolean(
        focused.closest('.dsp-nav__utility, .dsp-nav__shortcuts') ||
        (!this.desktop.matches && this.hasAttribute('data-nav-compact') && focused.closest('.dsp-nav__mobile-menu'))
      );
    }

    updateCompactSticky() {
      if (!this.flowSpacer || this.stickyLayoutIsBusy()) return;
      const scrollPosition = { x: window.scrollX, y: window.scrollY };
      let geometryChanged = false;
      const wasCompact = this.hasAttribute('data-nav-compact');
      if (this.pendingStickyMeasure || !this.expandedHeaderHeight) {
        // Measure the real expanded layout in one task; no intermediate state is painted.
        this.setAttribute('data-nav-measuring', '');
        this.removeAttribute('data-nav-compact');
        this.expandedHeaderHeight = this.headerSection.getBoundingClientRect().height;
        this.toggleAttribute('data-nav-compact', wasCompact);
        this.removeAttribute('data-nav-measuring');
        this.headerSection.style.setProperty('--dsp-nav-expanded-height', `${this.expandedHeaderHeight}px`);
        this.pendingStickyMeasure = false;
        this.syncStickyGeometry();
        geometryChanged = true;
        // A normal-flow spacer keeps this threshold stable even at a restored scroll position.
        this.compactScrollThreshold = this.flowSpacer.getBoundingClientRect().bottom + window.scrollY;
      }
      const scrollTop = Math.max(0, scrollPosition.y);
      const threshold = Math.max(24, this.compactScrollThreshold || this.expandedHeaderHeight);
      const compact = wasCompact ? scrollTop > Math.max(0, threshold - 32) : scrollTop > threshold + 8;
      if (compact !== wasCompact) {
        this.toggleAttribute('data-nav-compact', compact);
        this.headerSection.classList.toggle('scrolled-past-header', compact);
        this.syncStickyGeometry();
        geometryChanged = true;
      }
      this.observedNavWidth = this.offsetWidth;
      this.observedNavHeight = this.offsetHeight;
      if (geometryChanged) {
        // Intermediate height reads can trigger scroll anchoring before the spacer
        // restores the final flow. Keep the user's scroll position for this batch.
        this.flowSpacer.getBoundingClientRect();
        if (window.scrollX !== scrollPosition.x || window.scrollY !== scrollPosition.y) {
          window.scrollTo({ left: scrollPosition.x, top: scrollPosition.y, behavior: 'instant' });
        }
      }
    }

    syncStickyGeometry() {
      const visibleHeight = this.headerSection.getBoundingClientRect().height;
      this.flowSpacer.style.height = `${Math.max(0, this.expandedHeaderHeight - visibleHeight)}px`;
      this.sticky?.setHeaderHeight?.();
    }

    clearPointerTimer() {
      window.clearTimeout(this.pointerTimer);
      this.pointerTimer = null;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;
      const trigger = target.closest('[data-nav-trigger]');
      if (trigger && this.contains(trigger) && this.desktop.matches) {
        event.preventDefault();
        this.clearPointerTimer();
        if (this.openPanel?.id === trigger.dataset.navTrigger && this.openMethod !== 'pointer') {
          this.closePanel();
        } else {
          this.showPanel(trigger, 'click');
        }
        return;
      }
      const mobileOpen = target.closest('[data-nav-mobile-open]');
      if (mobileOpen && this.contains(mobileOpen) && !this.desktop.matches) {
        event.preventDefault();
        this.showDrawer(mobileOpen.dataset.navMobileOpen || 'home', mobileOpen);
        return;
      }
      if (target.closest('[data-nav-drawer-close]')) {
        event.preventDefault();
        this.closeDrawer({ restoreFocus: true });
      } else if (target.closest('[data-nav-back]')) {
        event.preventDefault();
        const page = this.pages.find((item) => item.dataset.navPage === this.currentPage);
        this.showPage(page?.dataset.parent || 'home');
      } else if (target.closest('[data-nav-close]')) {
        event.preventDefault();
        this.closePanel({ restoreFocus: true });
      } else if (target.closest('a[href]') && !target.closest('[data-dsp-open]')) {
        // Keep the existing cart click handler and normal link navigation untouched.
        this.closeAll({ restoreFocus: false });
      }
    }

    onPointerOver(event) {
      if (!this.desktop.matches || event.pointerType === 'touch') return;
      const target = event.target instanceof Element ? event.target : null;
      const trigger = target?.closest('[data-nav-trigger]');
      if (trigger && this.contains(trigger) && !trigger.contains(event.relatedTarget)) {
        this.clearPointerTimer();
        this.showPanel(trigger, 'pointer');
      } else {
        const leaf = target?.closest('[data-nav-leaf]');
        if (leaf && this.contains(leaf) && !leaf.closest('[data-nav-panel], [data-nav-drawer]') && !leaf.contains(event.relatedTarget)) this.closePanel();
      }
    }

    onKeyDown(event) {
      const trigger = event.target instanceof Element ? event.target.closest('[data-nav-trigger]') : null;
      if (trigger && this.desktop.matches && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        this.showPanel(trigger, 'keyboard');
        const items = Array.from(this.openPanel?.querySelectorAll(FOCUSABLE) || []).filter(isVisible);
        (event.key === 'ArrowUp' ? items.at(-1) : items[0])?.focus();
      }
      if (!this.drawer?.open || event.key !== 'Tab') return;
      const items = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(isVisible);
      const first = items[0];
      const last = items.at(-1);
      if (!first) {
        event.preventDefault();
        this.drawer.focus();
      } else if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    showPanel(trigger, method) {
      const panel = this.panels.find((item) => item.id === trigger.dataset.navTrigger);
      if (!panel || !this.desktop.matches) return;
      if (this.openPanel === panel) {
        if (method !== 'pointer') this.openMethod = method;
        return;
      }
      this.closePanel();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.openPanel = panel;
      this.panelTrigger = trigger;
      this.openMethod = method;
      this.setAttribute('data-open', panel.id);
      this.syncStickyState();
    }

    closePanel({ restoreFocus = false } = {}) {
      this.clearPointerTimer();
      const trigger = this.panelTrigger;
      if (this.openPanel) this.openPanel.hidden = true;
      this.triggers?.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      this.openPanel = null;
      this.panelTrigger = null;
      this.openMethod = null;
      this.removeAttribute('data-open');
      this.syncStickyState();
      if (restoreFocus && isVisible(trigger)) trigger.focus({ preventScroll: true });
    }

    showDrawer(pageId, opener) {
      if (!this.drawer || this.desktop.matches) return;
      this.closePanel();
      if (!this.drawer.open) {
        this.drawerOpener = opener;
        this.acquireScrollLock();
        try {
          this.drawer.showModal();
        } catch (error) {
          this.releaseScrollLock();
          throw error;
        }
      }
      this.setAttribute('data-drawer-open', '');
      this.setMobileExpanded(true);
      this.syncStickyState();
      this.showPage(pageId);
    }

    showPage(pageId) {
      const page = this.pages.find((item) => item.dataset.navPage === pageId) || this.pages.find((item) => item.dataset.navPage === 'home');
      if (!page) return;
      this.currentPage = page.dataset.navPage;
      this.pages.forEach((item) => { item.hidden = item !== page; });
      const back = this.querySelector('[data-nav-back]');
      if (back) {
        back.hidden = this.currentPage === 'home';
        const parent = this.pages.find((item) => item.dataset.navPage === (page.dataset.parent || 'home'));
        const label = parent?.querySelector('h2, h3')?.textContent?.trim() || 'Menu';
        back.setAttribute('aria-label', `Back to ${label}`);
        const text = back.querySelector('[data-nav-back-label]');
        if (text) text.textContent = label;
      }
      const scrollArea = page.closest('[data-nav-pages], .dsp-nav__pages');
      if (scrollArea) scrollArea.scrollTop = 0;
      const heading = page.querySelector('h2[tabindex="-1"], h3[tabindex="-1"], [data-nav-heading]');
      heading?.focus({ preventScroll: true });
    }

    setMobileExpanded(expanded) {
      this.querySelectorAll('[data-nav-mobile-open]').forEach((button) => {
        if (!this.drawer?.contains(button)) button.setAttribute('aria-expanded', String(expanded));
      });
    }

    closeDrawer({ restoreFocus = false } = {}) {
      const opener = this.drawerOpener;
      if (this.drawer?.open) this.drawer.close();
      this.releaseScrollLock();
      this.removeAttribute('data-drawer-open');
      this.setMobileExpanded(false);
      this.drawerOpener = null;
      this.syncStickyState();
      if (restoreFocus) {
        const fallback = this.querySelector('[data-nav-mobile-open="home"]');
        (isVisible(opener) ? opener : isVisible(fallback) ? fallback : null)?.focus({ preventScroll: true });
      }
    }

    closeAll(options = {}) {
      this.closePanel(options);
      this.closeDrawer(options);
    }

    onAssistantOpen(event) {
      const detail = event.detail;
      if (!detail || typeof detail !== 'object') return;
      const source = detail.sourceElement;
      const fromDrawer = source instanceof Node && this.drawer?.contains(source);
      if (fromDrawer) {
        const persistent = Array.from(this.querySelectorAll('[data-nav-search]')).find((element) => !this.drawer.contains(element) && isVisible(element));
        const menu = this.querySelector('[data-nav-mobile-open="home"]');
        const fallback = persistent || (isVisible(menu) ? menu : null);
        if (fallback) detail.returnFocusElement = fallback;
      }
      // Synchronous cleanup lets the bridge take over focus and scroll ownership cleanly.
      this.closeAll({ restoreFocus: false });
    }

    acquireScrollLock() {
      if (this.scrollLock) return;
      const body = document.body;
      const properties = ['overflow', 'padding-right', 'position', 'top', 'left', 'width'];
      const saved = properties.map((name) => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]);
      this.scrollLock = { saved, x: window.scrollX, y: window.scrollY };
      const gutter = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.setProperty('overflow', 'hidden');
      body.style.setProperty('padding-right', `${padding + gutter}px`);
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-this.scrollLock.y}px`);
      body.style.setProperty('left', `${-this.scrollLock.x}px`);
      body.style.setProperty('width', '100%');
    }

    releaseScrollLock() {
      if (!this.scrollLock) return;
      const { saved, x, y } = this.scrollLock;
      this.scrollLock = null;
      saved.forEach(([name, value, priority]) => {
        if (value) document.body.style.setProperty(name, value, priority);
        else document.body.style.removeProperty(name);
      });
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
    }

    syncStickyState() {
      const active = Boolean(this.openPanel || this.drawer?.open);
      if (active && !this.stickyState) {
        this.stickyState = {
          preventHide: this.sticky?.preventHide,
          hadMenuOpen: this.headerSection?.classList.contains('menu-open') || false,
        };
        if (this.sticky) this.sticky.preventHide = true;
        this.headerSection?.classList.add('menu-open');
      } else if (!active && this.stickyState) {
        if (this.sticky) this.sticky.preventHide = this.stickyState.preventHide;
        if (!this.stickyState.hadMenuOpen) this.headerSection?.classList.remove('menu-open');
        this.stickyState = null;
      }
      this.queueStickyUpdate();
    }
  }

  customElements.define('dsp-navigation', DSPNavigation);
})();
